import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface SelectAllDeselectAllProps {
  totalOptions: number;
  selectedCount: number;
  onSelectAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onDeselectAll: (event: React.MouseEvent<HTMLButtonElement>) => void;
  children?: ReactNode;
}

export const SelectAllDeselectAll = ({
  totalOptions,
  selectedCount,
  onSelectAll,
  onDeselectAll,
}: SelectAllDeselectAllProps) => {
  const { t } = useTranslation();
  const allSelected = selectedCount === totalOptions;
  const noneSelected = selectedCount === 0;

  // Prevent MUI Autocomplete from closing the dropdown when clicking these buttons
  // MUI listens to onMouseDown events to detect clicks outside the listbox
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        gap: 2,
        px: 1,
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.divider}`,
        justifyContent: 'flex-start',
        alignItems: 'center',
      })}
      data-test="select-all-deselect-all-row"
    >
      <Button
        size="small"
        variant="text"
        onMouseDown={handleMouseDown}
        onClick={onSelectAll}
        disabled={allSelected}
        sx={theme => ({
          color: allSelected ? theme.palette.action.disabled : theme.palette.primary.main,
          textTransform: 'uppercase',
          fontWeight: 'fontWeightBold',
          cursor: allSelected ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: allSelected ? 'transparent' : `${theme.palette.primary.main}14`,
          },
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
          },
        })}
        data-test="select-all-button"
      >
        {t('Select all')} ({totalOptions})
      </Button>

      <Button
        size="small"
        variant="text"
        onMouseDown={handleMouseDown}
        onClick={onDeselectAll}
        disabled={noneSelected}
        sx={theme => ({
          color: noneSelected ? theme.palette.action.disabled : theme.palette.error.main,
          textTransform: 'uppercase',
          fontWeight: 'fontWeightBold',
          cursor: noneSelected ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: noneSelected ? 'transparent' : `${theme.palette.error.main}14`,
          },
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
          },
        })}
        data-test="deselect-all-button"
      >
        {t('Deselect all')}
      </Button>
    </Box>
  );
};
